import React, { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import ToastMessage from '@/components/common/toast/ToastMessage'
import CommonModal from '@/components/common/modal/CommonModal'

interface ErrorContextType {
  showErrorToast: (message: string) => void
  showErrorModal: (message: string, onConfirm?: () => void) => void
  showLoginRedirectModal: (message: string) => void
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined)

export const useErrorHandler = () => {
  const context = useContext(ErrorContext)
  if (!context) {
    throw new Error('useErrorHandler는 ErrorProvider 내에서 사용되어야 합니다.')
  }
  return context
}

interface ErrorProviderProps {
  children: ReactNode
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const navigate = useNavigate()

  // 토스트 상태
  const [toast, setToast] = useState<{
    isVisible: boolean
    message: string
  }>({
    isVisible: false,
    message: ''
  })

  // 모달 상태
  const [modal, setModal] = useState<{
    isVisible: boolean
    message: string
    type: 'error' | 'login-redirect'
    onConfirm?: () => void
  }>({
    isVisible: false,
    message: '',
    type: 'error'
  })

  const showErrorToast = (message: string) => {
    setToast({
      isVisible: true,
      message
    })
  }

  const showErrorModal = (message: string, onConfirm?: () => void) => {
    setModal({
      isVisible: true,
      message,
      type: 'error',
      onConfirm
    })
  }

  const showLoginRedirectModal = (message: string) => {
    setModal({
      isVisible: true,
      message,
      type: 'login-redirect',
      onConfirm: () => {
        navigate('/auth/login')
      }
    })
  }

  const handleToastClose = () => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }))
  }

  const handleModalClose = () => {
    setModal(prev => ({
      ...prev,
      isVisible: false
    }))
  }

  const handleModalConfirm = () => {
    if (modal.onConfirm) {
      modal.onConfirm()
    }
    handleModalClose()
  }

  return (
    <ErrorContext.Provider
      value={{
        showErrorToast,
        showErrorModal,
        showLoginRedirectModal
      }}
    >
      {children}

      {/* 토스트 메시지 */}
      <ToastMessage message={toast.message} isVisible={toast.isVisible} onClose={handleToastClose} />

      {/* 에러 모달 */}
      {modal.isVisible && (
        <CommonModal
          hasCloseButton={false}
          onClose={handleModalClose}
          topButtonText={modal.type === 'login-redirect' ? '로그인하기' : '확인'}
          bottomButtonText='취소'
          topButtonOnClick={handleModalConfirm}
          bottomButtonOnClick={handleModalClose}
          isTopButtonDisabled={false}
          modalContent={
            <div className='text-center mb-6'>
              <div className='w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center'>
                <svg className='w-8 h-8 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <p className='text-gray-700 text-sm leading-relaxed'>{modal.message}</p>
            </div>
          }
        />
      )}
    </ErrorContext.Provider>
  )
}
